<template>
  <router-link
    v-if="recent.id"
    :to="route"
    :aria-label="header"
    class="recently-viewed-result flex flex-column"
  >
    <div
      class="recently-viewed-top-line flex justify-content-between align-items-center"
    >
      <div class="recently-viewed-header flex flex-gap-1 align-items-center">
        <DomainAvatar
          :domain="recent.selectedDomain"
          :domain-branding="recent.selectedDomainBranding"
          size="normal"
        />
        <h1 class="text-base font-medium">
          {{ header }}
        </h1>
      </div>
      <span class="last-viewed-time text-gray-darker text-xs">
        {{ recent.getRelativeTime() }}
      </span>
    </div>

    <div
      class="recently-viewed-bottom-line flex flex-gap-1 align-items-center text-sm"
    >
      <span v-if="recent.scac" class="scac">
        {{ recent.scac }}
      </span>

      <i
        v-if="recent.scac && firstDate"
        class="pi pi-circle-fill"
        aria-hidden="true"
      />

      <span v-if="firstDate" class="ship-date">
        {{ t(`enumMap.DateType.${firstDate.type}`) }}:
        {{ firstDate.dateTime ? d(firstDate.dateTime, 'numeric') : '-' }}
      </span>
    </div>
  </router-link>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { DomainBranding } from '@rygen/rygen-ui/model';

import {
  QuickSearchDate,
  QuickSearchDateType,
  RecentlyViewedResult,
} from '@/model';
import { useDomainStore } from '@/stores/Domain';

import DomainAvatar from '@rygen/rygen-ui/components/domain/DomainAvatar.vue';

export type DomainBrandingRefreshPayload = {
  result: RecentlyViewedResult;
  domainBranding: DomainBranding;
};

const props = defineProps({
  recent: {
    type: Object as PropType<RecentlyViewedResult>,
    required: true,
  },
});
const { recent } = toRefs(props);
const emit = defineEmits<{
  (
    event: 'domain-branding-refresh',
    payload: DomainBrandingRefreshPayload,
  ): void;
}>();

const { domainBrandings, loadDomainBranding } = useDomainStore();
const { d, t } = useI18n();

const isOrder = computed(() => recent.value.type === 'ORDER');

const header = computed(() => {
  return isOrder.value
    ? t('orders.orderNumParam', { param: recent.value?.id })
    : t('shipments.shipmentNumParam', { param: recent.value?.id });
});

const route = computed(() => {
  return isOrder.value
    ? { name: 'orderDetail', params: { orderId: recent.value.id } }
    : { name: 'shipmentDetail', params: { shipmentId: recent.value.id } };
});

const firstDate = computed(() => {
  if (recent.value.actualArrival) {
    return new QuickSearchDate({
      dateTime: new Date(recent.value.actualArrival.toJSDate()),
      type: QuickSearchDateType.ACTUAL_ARRIVAL,
    });
  } else if (recent.value.expectedArrival) {
    return new QuickSearchDate({
      dateTime: new Date(recent.value.expectedArrival.toJSDate()),
      type: QuickSearchDateType.EXPECTED_ARRIVAL,
    });
  } else if (recent.value.scheduledAppointmentEarliest) {
    return new QuickSearchDate({
      dateTime: new Date(recent.value.scheduledAppointmentEarliest.toJSDate()),
      type: QuickSearchDateType.SCHEDULED_APPOINTMENT_EARLIEST,
    });
  } else if (recent.value.scheduledAppointmentLatest) {
    return new QuickSearchDate({
      dateTime: new Date(recent.value.scheduledAppointmentLatest.toJSDate()),
      type: QuickSearchDateType.SCHEDULED_APPOINTMENT_LATEST,
    });
  } else if (recent.value.requestedAppointmentEarliest) {
    return new QuickSearchDate({
      dateTime: new Date(recent.value.requestedAppointmentEarliest.toJSDate()),
      type: QuickSearchDateType.REQUESTED_APPOINTMENT_EARLIEST,
    });
  } else if (recent.value.requestedAppointmentLatest) {
    return new QuickSearchDate({
      dateTime: new Date(recent.value.requestedAppointmentLatest.toJSDate()),
      type: QuickSearchDateType.REQUESTED_APPOINTMENT_LATEST,
    });
  } else {
    return undefined;
  }
});

const domainId = computed(
  () =>
    recent.value.selectedDomain?.id ??
    recent.value.selectedDomainBranding?.domainId,
);

const isMissingOrExpired = computed(() => {
  const branding = recent.value.selectedDomainBranding;
  return !branding || !!branding.avatar?.isExpired;
});

const loadedDomainBranding = computed(() =>
  domainId.value ? domainBrandings.value[domainId.value]?.data : undefined,
);

const checkBrandAvatarExpiration = async (result: RecentlyViewedResult) => {
  if (!isMissingOrExpired.value) {
    return;
  }

  if (!domainId.value) {
    console.warn(`Recent ${result.id} does not have a selected domain id`);
    return;
  }

  await loadDomainBranding(domainId.value);
};

watch(
  recent,
  async (newVal: RecentlyViewedResult) => {
    await checkBrandAvatarExpiration(newVal);
  },
  { immediate: true },
);

watch(
  loadedDomainBranding,
  (domainBranding) => {
    if (domainBranding && isMissingOrExpired.value) {
      emit('domain-branding-refresh', { result: recent.value, domainBranding });
    }
  },
  { immediate: true },
);
</script>

<style>
a.recently-viewed-result {
  --recently-viewed-header-color: var(--p-surface-600);
  --recently-viewed-text-color: var(--p-surface-400);
  --recently-viewed-background-color: initial;

  background: var(--recently-viewed-background-color);
  color: var(--recently-viewed-text-color);
  gap: var(--sp-1);
  text-decoration: none;

  &:hover {
    --recently-viewed-header-color: var(--p-surface-800);
    --recently-viewed-text-color: var(--p-surface-600);
    --recently-viewed-background-color: var(--p-surface-200);
  }

  .recently-viewed-top-line {
    h1 {
      color: var(--recently-viewed-header-color);
    }
  }

  .recently-viewed-bottom-line {
    margin-left: calc(var(--p-avatar-width) + var(--gap-1));

    i.pi {
      font-size: 4px;
    }
  }
}
</style>
